import { campaignService } from "@/api/campaignService";
import { packageService } from "@/api/packageService";
import type { CampaignLineItemChanges } from "@/resources/campaign";
import type { PackageWithId } from "@/resources/package";

const loadedPackages: {
  [key: string]: PackageWithId | null;
} = {};

export async function getPackageById(packageId: string, forceRefresh = false) {
  if (forceRefresh) {
    delete loadedPackages[packageId];
  }

  let thisPackage = loadedPackages[packageId];

  if (!thisPackage) {
    thisPackage = await packageService.getByIdOrCode(packageId);
    if (!thisPackage) {
      return null;
    }
    loadedPackages[packageId] = thisPackage;
  }

  return {
    ...thisPackage,
    id: thisPackage.id,
  };
}

export async function invalidatePackageById(packageId: string) {
  delete loadedPackages[packageId];
}

export async function deletePackage(packageId: string, campaignIds: string[]) {
  if (!packageId) {
    throw new Error("Package ID is not defined");
  }

  const result = await packageService.deleteByIdOrCode(packageId);
  if (!result) {
    throw new Error("Failed to delete package");
  }

  const campaignDeletionResults = await Promise.all(
    campaignIds.map(async (campaignId) => {
      return await campaignService.deleteByIdOrCode(campaignId);
    }),
  );

  if (campaignDeletionResults.some((result) => !result)) {
    // No revert logic, probably should not throw an error?
    throw new Error("Failed to delete campaigns");
  }
}

export async function savePackageChanges(
  campaignUpdates: CampaignLineItemChanges[],
  currentPackage: PackageWithId | null,
  packageOpenAtCloseAtValues: {
    openAt: string;
    closedAt: string;
  },
) {
  if (!currentPackage?.id) {
    return;
  }
  const campaignIds = campaignUpdates
    .filter((campaign) => campaign.operation !== "delete")
    .map((campaign) => campaign.id)
    .filter((id): id is string => id !== undefined);

  const packagePayload = {
    name: currentPackage.name,
    code: currentPackage.code,
    ...packageOpenAtCloseAtValues,
    campaignIds,
  };

  const result = await packageService.updateByIdOrCode(
    currentPackage.id,
    packagePayload,
  );
  if (!result) {
    throw new Error("Failed to save package changes");
  }
}
